import { Box, ButtonProps, useMediaQuery, useTheme } from "@mui/material";
import { GridAddIcon } from "@mui/x-data-grid";
import { useQueryClient } from "@tanstack/react-query";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { ReactComponent as Export } from "../../assets/icons/export.svg";
import { COLORS } from "../../assets/theme";
import ToastNotification from "../../components/ToastNotification";
import Actions from "../../components/common/Actions";
import CustomDialog from "../../components/common/CustomDialog";
import CustomLabel from "../../components/common/CustomLabel";
import CustomTable from "../../components/common/table/CustomTable";
import SearchHeaderContainer from "../../components/common/table/SearchHeaderContainer";
import {
  BankDetailsPosition,
  createInvoiceTemplate,
  exportInvoiceTempalte,
  fetchInvoiceTemplate,
  fetchInvoiceTemplateHistory,
  InvoiceTemplateHistory,
  InvoiceTemplateType,
  updateInvoiceTemplate,
  useInvoiceTemplateQuery,
} from "../../services/InvoiceTemplateService";
import { IField, Params } from "../../types/table/interfaces.type";
import History from "../../components/History";
import SearchForm from "../../components/common/SearchForm";
import { IAutoCompleteProps } from "../../components/common/AutoComplete";
import { hasJwtPermission } from "../../services/ServicesHelpers";

const InvoiceTemplatePage: React.FC = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const { data } = useInvoiceTemplateQuery({});
  const names =
    data?.content?.map((account) => ({
      label: account.name,
      value: account.name,
    })) || [];
  const titles =
    data?.content?.map((account) => ({
      label: account.invoiceTitle,
      value: account.invoiceTitle,
    })) || [];
  const [appliedFilters, setAppliedFilters] = useState<
    Params<InvoiceTemplateType>
  >({
    search: "",
    name: "",
  });

  const [filters, setFilters] = useState<
    Params<
      InvoiceTemplateType & {
        name: string;
        invoiceTitle: string;
      }
    >
  >({
    name: "",
    invoiceTitle: "",
  });

  const dialogRef = useRef<InvoiceDialogRef>(null);

  const handleOpenDialog = (
    mode: "add" | "edit" | "delete" | "history",
    id?: string
  ) => {
    dialogRef.current?.openDialog(mode, id);
  };

  const handleSearchChange = (search: string) => {
    setAppliedFilters((prev) => ({ ...prev, search }));
  };

  const handleSearchClick = () => {
    setAppliedFilters((prev) => ({
      ...prev,
      name: filters.name,
      invoiceTitle: filters.invoiceTitle,
    }));
  };

  const actions = Actions({
    handleOpenEditModal: {
      handler: async (id: string | number) => {
        handleOpenDialog("edit", String(id));
      },
      isDisabled: !hasJwtPermission("GET_INVOICE_TEMPLATE"),
    },
    onViewHistory: {
      // isDisabled: true,
      handler: async (id: string | number) => {
        handleOpenDialog("history", String(id));
      },
      isDisabled: !hasJwtPermission("GET_ALL_INVOICE_TEMPLATE_HISTORY"),
    },
  });

  const autoCompletes: IAutoCompleteProps[] = [
    {
      label: "Name",
      value: filters.name ?? "",
      options: names,
      clearable: true,
      placeholder: "Name",
      onChange: (value: string | number | (string | number)[]) => {
        const selectedOption = names.find((option) => option.value === value);
        setFilters((prev) => ({
          ...prev,
          name: String(selectedOption?.value ?? ""),
        }));
      },
    },

    {
      label: "Invoice Title",
      value: filters.invoiceTitle ?? "",
      options: titles,
      clearable: true,
      placeholder: "Invoice Title",
      onChange: (value: string | number | (string | number)[]) => {
        const selectedOption = titles.find((option) => option.value === value);
        setFilters((prev) => ({
          ...prev,
          invoiceTitle: String(selectedOption?.value ?? ""),
        }));
      },
    },
  ];

  const buttons = [
    {
      label: "Search",
      sx: { marginTop: 2 },
      variant: "contained" as ButtonProps["variant"],
      bgColor: COLORS.GOLD,
      onClick: handleSearchClick,
      permission: "GET_ALL_INVOICE_TEMPLATES",
    },
  ];

  const fields: IField<InvoiceTemplateType>[] = [
    { key: "name", header: "Name" },
    { key: "invoiceTitle", header: "Invoice Title" },
    { key: "address", header: "Address" },
    { key: "bankDetails", header: "Bank Details" },
    { key: "createdDate", header: "Created Date" },
    // { key: "mccmnc", header: "MCCMNC" },
  ];

  return (
    <Box padding={isSmallScreen ? 2 : 3}>
      <Box sx={{ mb: 3 }}>
        <SearchForm autoCompletes={autoCompletes} buttons={buttons} />
        <CustomLabel
          text="Invoice Configuration List"
          variant={isSmallScreen ? "h6" : "h5"}
          fontWeight={500}
          mb={1}
        />
        <SearchHeaderContainer
          search={appliedFilters.search || ""}
          setSearch={handleSearchChange}
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <GridAddIcon />,
              onClick: () => handleOpenDialog("add"),
              textColor: COLORS.iconColor,
              permission: "POST_INVOICE_TEMPLATE",
            },
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <Export />,
              onClick: async () => {
                try {
                  await exportInvoiceTempalte(appliedFilters);
                  ToastNotification.success("Export completed successfully!");
                } catch (error: any) {
                  ToastNotification.error(
                    error.message || "Export failed. Please try again."
                  );
                }
              },
              showExportProgress: true,
              textColor: COLORS.iconColor,
              label: "Export",
              permission: "GET_ALL_INVOICE_TEMPLATE_CSV",
            },
          ]}
        />
        <CustomTable
          filters={appliedFilters}
          fields={fields}
          fetchFunc={useInvoiceTemplateQuery}
          actions={actions}
          sortableFields={[
            "name",
            "address",
            "billingEmail",
            "bankDetails",
            "createdDate",
          ]}
        />
      </Box>
      <InvoiceDialog
        onRefresh={() => {
          setAppliedFilters((prev) => ({ ...prev }));
          queryClient.invalidateQueries();
        }}
        ref={dialogRef}
        appliedFilters={appliedFilters}
      />
    </Box>
  );
};

interface InvoiceDialogRef {
  openDialog: (
    mode: "add" | "edit" | "delete" | "history",
    id?: string
  ) => void;
}

interface InvoiceDialogProps {
  onRefresh: () => void;
  appliedFilters: Params<InvoiceTemplateType>;
}

const InvoiceDialog = forwardRef<InvoiceDialogRef, InvoiceDialogProps>(
  ({ onRefresh, appliedFilters }, ref) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<
      "add" | "edit" | "delete" | "history"
    >("add");
    const [dialogData, setDialogData] = useState<Partial<InvoiceTemplateType>>(
      {}
    );
    const [isSaveDisabled, setIsSaveDisabled] = useState(true);
    const [currentStep, setCurrentStep] = useState(0);
    const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
    const [selectedId, setSelectedId] = useState<string | undefined>();

    useImperativeHandle(ref, () => ({
      openDialog: (mode, id) => {
        setDialogMode(mode);
        setDialogOpen(true);
        setSelectedId(id);
        openDialog(mode, id);
      },
    }));

    useEffect(() => {
      const isAnyFieldEmpty = Object.values(dialogData).some((value) => !value);
      setIsSaveDisabled(isAnyFieldEmpty);
    }, [dialogData]);

    useEffect(() => {
      const requiredFields = [
        "name",
        "address",
        "billingEmail",
        "invoiceReference",
        "invoiceTitle",
        "emailAddress",
        "bankDetails",
        "emailSender",
        "bankDetailsPosition",
        "emailPassword",
        "emailTemplate",
        "outgoingServer",
        "signatureTemplate",
        "port",
      ];
      const areRequiredFieldsFilled = requiredFields.every(
        (field) => !!dialogData[field as keyof InvoiceTemplateType]
      );
      setIsSaveDisabled(!areRequiredFieldsFilled);
    }, [dialogData]);

    useEffect(() => {
      const stepRequiredFields = {
        0: ["name", "address", "bankDetailsPosition", "bankDetails"],
      };

      const currentStepRequiredFields = stepRequiredFields[0] || [];

      const areCurrentStepFieldsValid = currentStepRequiredFields.every(
        (field) => {
          const value = dialogData[field as keyof InvoiceTemplateType];
          return (typeof value === "string" ? value : value) && !errors[field];
        }
      );
      setIsSaveDisabled(!areCurrentStepFieldsValid);
    }, [dialogData, errors, currentStep]);

    const addInvoiceTemplate = async (data: Partial<InvoiceTemplateType>) => {
      try {
        await createInvoiceTemplate(data);
        onRefresh();

        ToastNotification.success("Invoice template added successfully!");
      } catch (error: any) {
        ToastNotification.error(error.message);
      }
    };

    const editInvoiceTemplate = async (
      id: string,
      data: Partial<InvoiceTemplateType>
    ) => {
      try {
        await updateInvoiceTemplate({ ...data, id });
        onRefresh();
        setDialogOpen(false);

        ToastNotification.success("Invoice template updated successfully!");
      } catch (error: any) {
        ToastNotification.error(error.message);
      }
    };

    const openDialog = async (
      mode: "add" | "edit" | "delete" | "history",
      id?: string
    ) => {
      setCurrentStep(0);
      setDialogMode(mode);

      if (mode === "edit" && id) {
        try {
          const data = await fetchInvoiceTemplate(id);
          setDialogData(data);
        } catch (error: any) {
          console.error("Error fetching invoice template data:", error);
        }
      } else {
        setDialogData({});
      }
    };

    const handleSave = async () => {
      try {
        const dataToSave = { ...dialogData, isDeleted: false };

        if (dialogMode === "add") {
          await addInvoiceTemplate(dataToSave);
        } else {
          await editInvoiceTemplate(dataToSave.id as string, dataToSave);
        }

        setDialogOpen(false);
        setDialogData({});
        setCurrentStep(0);
        onRefresh();
      } catch (error: any) {
        console.error("Error saving invoice template:", error);
      }
    };
    const resetDialogData = () => {
      setDialogData({});
      setDialogOpen(false);
    };
    const fieldHistory: IField<InvoiceTemplateType>[] = [
      { key: "name", header: "Name" },
      { key: "invoiceTitle", header: "Invoice Title" },
      { key: "address", header: "Address" },
      { key: "bankDetails", header: "Bank Details" },
      { key: "bankDetailsPosition", header: "Bank Details Position" },
      { key: "createdDate", header: "Created Date" },
      { key: "createdBy", header: "Created By" },
      { key: "historyTimestamp", header: "Date Modified" },
      { key: "createdBy", header: "Created By" },
      { key: "updatedBy", header: "Updated By" },
    ];
    return (
      <CustomDialog
        open={dialogOpen}
        onClose={resetDialogData}
        mode={dialogMode}
        title={dialogMode === "history" ? "History" : "Add Invoice Template"}
        fields={
          dialogMode === "history"
            ? [
                {
                  type: "custom",
                  component: (
                    <History
                      filters={appliedFilters}
                      fields={fieldHistory}
                      fetchFunc={({ size = 10 }) =>
                        fetchInvoiceTemplateHistory(String(selectedId), {
                          ...{
                            historyTimestamp:
                              appliedFilters.historyTimestamp instanceof Date
                                ? appliedFilters.historyTimestamp.toISOString()
                                : appliedFilters.historyTimestamp,
                          },
                          size,
                        })
                      }
                    />
                  ),
                },
              ]
            : [
                {
                  type: "text",
                  label: "Invoice Template Name",
                  placeholder: "Name",
                  bgColor: COLORS.WhiteBackground,
                  highlightLabelStar: true,
                  error: errors.name || false,
                  helperText: errors.name ? "This field is required." : "",
                  value: dialogData.name || "",
                  onChange: (value) => {
                    const trimmedValue = String(value).trimStart();
                    setDialogData((prev) => ({
                      ...prev,
                      name: trimmedValue,
                    }));
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      name: !trimmedValue,
                    }));
                  },
                },
                {
                  type: "text",
                  label: "Address",
                  placeholder: "Address",
                  bgColor: COLORS.WhiteBackground,
                  highlightLabelStar: true,
                  error: errors.address || false,
                  helperText: errors.address ? "This field is required." : "",
                  value: dialogData.address || "",
                  onChange: (value) => {
                    const trimmedValue = String(value).trimStart();
                    setDialogData((prev) => ({
                      ...prev,
                      address: trimmedValue,
                    }));
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      address: !trimmedValue,
                    }));
                  },
                },
                {
                  type: "text",
                  label: "Invoice Title",
                  bgColor: COLORS.WhiteBackground,
                  value: dialogData.invoiceTitle || "",
                  placeholder: "Invoice Title",
                  highlightLabelStar: true,
                  error: errors.invoiceTitle || false,
                  helperText: errors.invoiceTitle
                    ? "This field is required."
                    : "",
                  onChange: (value) => {
                    const trimmedValue = String(value).trimStart();
                    setDialogData((prev) => ({
                      ...prev,
                      invoiceTitle: trimmedValue,
                    }));
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      invoiceTitle: !trimmedValue,
                    }));
                  },
                },

                {
                  type: "text",
                  label: "Bank Details",
                  bgColor: COLORS.WhiteBackground,
                  value: dialogData.bankDetails || "",
                  placeholder: "Bank Details",
                  rows: 6,
                  highlightLabelStar: true,
                  error: errors.bankDetails || false,
                  helperText: errors.bankDetails
                    ? "This field is required."
                    : "",
                  onChange: (value) => {
                    const trimmedValue = String(value).trimStart();
                    setDialogData((prev) => ({
                      ...prev,
                      bankDetails: trimmedValue,
                    }));
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      bankDetails: !trimmedValue,
                    }));
                  },
                  onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
                    const target = e.target as HTMLTextAreaElement;
                    if (
                      e.key === "Enter" &&
                      typeof target.selectionStart === "number" &&
                      typeof target.value === "string"
                    ) {
                      const value = target.value;
                      if (target.selectionStart === value.length) {
                        setDialogData((prev) => ({
                          ...prev,
                          bankDetails: (prev.bankDetails || "") + "\n",
                        }));
                        e.preventDefault();
                      }
                    }
                  },
                },
                {
                  type: "select",
                  options: Object.values(BankDetailsPosition)
                    .filter((value) => typeof value === "string")
                    .map((position) => ({
                      label: position as string,
                      value: position as string,
                    })),
                  label: "Bank Details Position",
                  bgColor: COLORS.WhiteBackground,
                  value: dialogData.bankDetailsPosition?.toString() || "",
                  placeholder: "Bank Details Position",
                  highlightLabelStar: true,
                  error: errors.bankDetailsPosition || false,
                  helperText: errors.bankDetailsPosition
                    ? "This field is required."
                    : "",
                  onChange: (value) => {
                    setDialogData((prev) => ({
                      ...prev,
                      bankDetailsPosition: value as BankDetailsPosition,
                    }));
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      bankDetailsPosition: !value,
                    }));
                  },
                },
              ]
        }
        buttons={
          dialogMode === "history"
            ? []
            : [
                {
                  label: "Save",
                  variant: "contained" as const,
                  bgColor: COLORS.GOLD,
                  onClick: handleSave,
                  disabled:
                    isSaveDisabled ||
                    (dialogMode === "edit"
                      ? !hasJwtPermission("PUT_INVOICE_TEMPLATE")
                      : !hasJwtPermission("POST_INVOICE_TEMPLATE")),
                },
              ]
        }
      />
    );
  }
);

export default InvoiceTemplatePage;
